/**
 * ActionsList.jsx
 * ===============
 * Component for displaying and managing saved actions
 * Shows action metadata, preview, and management options
 * 
 * Created: 2025-11-20
 */

import React, { useState, useEffect } from 'react';
import { useAppStore } from '../../store/useAppStore';
import ActionElementRenderer from './ActionElementRenderer';
import { executeAction } from '../../utils/actionExecutor';
import apiService from '../../services/apiService';
import { createLogger } from '../../utils/logger';

const logger = createLogger('components/actions/ActionsList.jsx');

const ActionsList = ({ onEdit = null, refreshTrigger = 0 }) => {
  const { appName } = useAppStore();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedActionId, setExpandedActionId] = useState(null);

  useEffect(() => {
    loadActions();
  }, [appName, refreshTrigger]);

  const loadActions = async () => {
    if (!appName) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      logger.info('loadActions', 'Loading actions', { appName });
      const data = await apiService.getActions(appName);
      setActions(data);
      logger.info('loadActions', 'Actions loaded', { count: data.length });
    } catch (error) {
      logger.error('loadActions', 'Failed to load actions', error);
      console.error('Failed to load actions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (actionId, actionName) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete action "${actionName}"?\n\nThis action will be removed from the library, but components using it will retain their copy.`
    );

    if (!confirmed) return;

    try {
      logger.info('handleDelete', 'Deleting action', { appName, actionId });
      await apiService.deleteAction(appName, actionId);
      alert(`✅ Action "${actionName}" deleted successfully`);
      loadActions(); // Refresh list
    } catch (error) {
      logger.error('handleDelete', 'Failed to delete action', error);
      alert('❌ Failed to delete action. Check console for details.');
      console.error('Delete action error:', error);
    }
  };

  const handleTest = (action) => {
    logger.info('handleTest', 'Testing action', { actionId: action.actionId });
    executeAction(action);
  };

  const toggleExpand = (actionId) => {
    setExpandedActionId(expandedActionId === actionId ? null : actionId);
  };

  const viewJSON = (action) => {
    console.group(`📋 Action JSON: ${action.name}`);
    console.log(JSON.stringify(action, null, 2));
    console.groupEnd();
    alert(`Action JSON logged to console.\n\nAction: ${action.name}\nID: ${action.actionId}`);
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <p style={styles.loadingText}>Loading actions...</p>
      </div>
    );
  }

  if (actions.length === 0) {
    return (
      <div style={styles.emptyContainer}>
        <p style={styles.emptyText}>No actions configured yet.</p>
        <p style={styles.emptySubtext}>Create your first action to get started.</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Saved Actions ({actions.length})</h2>
      </div>

      <div style={styles.actionsList}>
        {actions.map((action) => {
          const isExpanded = expandedActionId === action.actionId;
          
          return (
            <div key={action.actionId} style={styles.actionCard}>
              {/* Action Header */}
              <div style={styles.actionHeader}>
                <div style={styles.actionInfo}>
                  <h3 style={styles.actionName}>{action.name}</h3>
                  <div style={styles.actionMeta}>
                    <span style={styles.metaBadge}>
                      {action.actionType?.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                    <span style={styles.metaSeparator}>•</span>
                    <span style={styles.metaText}>
                      {action.triggerElement} on {action.triggerEvent}
                    </span>
                    {action.version > 1 && (
                      <>
                        <span style={styles.metaSeparator}>•</span>
                        <span style={styles.versionBadge}>v{action.version}</span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => toggleExpand(action.actionId)}
                  style={styles.expandButton}
                >
                  {isExpanded ? '▼' : '▶'}
                </button>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div style={styles.expandedContent}>
                  {/* Preview */}
                  <div style={styles.previewSection}>
                    <p style={styles.sectionLabel}>Preview:</p>
                    <div style={styles.previewBox}>
                      <ActionElementRenderer
                        actionConfig={action}
                        isLive={false}
                        onExecute={null}
                      />
                    </div>
                  </div>

                  {/* Action Details */}
                  <div style={styles.detailsSection}>
                    <p style={styles.sectionLabel}>Details:</p>
                    <div style={styles.detailsList}>
                      <div style={styles.detailItem}>
                        <strong>Action ID:</strong> {action.actionId}
                      </div>
                      <div style={styles.detailItem}>
                        <strong>Created:</strong> {new Date(action.createdAt).toLocaleString()}
                      </div>
                      <div style={styles.detailItem}>
                        <strong>Updated:</strong> {new Date(action.updatedAt).toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={styles.actionButtons}>
                    <button
                      onClick={() => handleTest(action)}
                      style={styles.testButton}
                    >
                      🚀 Test Action
                    </button>
                    {onEdit && (
                      <button
                        onClick={() => onEdit(action)}
                        style={styles.editButton}
                      >
                        ✏️ Edit
                      </button>
                    )}
                    <button
                      onClick={() => viewJSON(action)}
                      style={styles.jsonButton}
                    >
                      📋 View JSON
                    </button>
                    <button
                      onClick={() => handleDelete(action.actionId, action.name)}
                      style={styles.deleteButton}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Inline styles
const styles = {
  container: {
    marginTop: '32px',
  },
  header: {
    marginBottom: '16px',
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '700',
    color: '#111827',
  },
  loadingContainer: {
    padding: '40px',
    textAlign: 'center',
  },
  loadingText: {
    color: '#6B7280',
    fontSize: '14px',
  },
  emptyContainer: {
    padding: '60px 20px',
    textAlign: 'center',
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    border: '2px dashed #D1D5DB',
  },
  emptyText: {
    margin: '0 0 8px 0',
    fontSize: '16px',
    color: '#6B7280',
    fontWeight: '600',
  },
  emptySubtext: {
    margin: 0,
    fontSize: '14px',
    color: '#9CA3AF',
  },
  actionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  actionCard: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    padding: '16px',
    transition: 'box-shadow 0.2s',
    cursor: 'pointer',
  },
  actionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  actionInfo: {
    flex: 1,
  },
  actionName: {
    margin: '0 0 8px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
  },
  actionMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  metaBadge: {
    backgroundColor: '#DBEAFE',
    color: '#1E40AF',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  versionBadge: {
    backgroundColor: '#FEF3C7',
    color: '#92400E',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
  },
  metaText: {
    fontSize: '13px',
    color: '#6B7280',
  },
  metaSeparator: {
    color: '#D1D5DB',
  },
  expandButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '16px',
    color: '#6B7280',
    cursor: 'pointer',
    padding: '4px 8px',
  },
  expandedContent: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #E5E7EB',
  },
  previewSection: {
    marginBottom: '16px',
  },
  sectionLabel: {
    margin: '0 0 8px 0',
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
  },
  previewBox: {
    backgroundColor: '#F9FAFB',
    padding: '20px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '80px',
    border: '1px solid #E5E7EB',
  },
  detailsSection: {
    marginBottom: '16px',
  },
  detailsList: {
    backgroundColor: '#F9FAFB',
    padding: '12px',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#374151',
  },
  detailItem: {
    marginBottom: '6px',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  testButton: {
    backgroundColor: '#8B5CF6',
    color: '#FFFFFF',
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  editButton: {
    backgroundColor: '#3B82F6',
    color: '#FFFFFF',
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  jsonButton: {
    backgroundColor: '#10B981',
    color: '#FFFFFF',
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  deleteButton: {
    backgroundColor: '#EF4444',
    color: '#FFFFFF',
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default ActionsList;





